import React from 'react'
import {
    Box,
    Button,
    Chip,
    Drawer,
    IconButton,
    InputAdornment,
    makeStyles,
    MenuItem,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import { useUserTags } from '../../contexts/UserTagsContext'
import { UserTag } from '../../models/Users/types'
import CloseIcon from '@material-ui/icons/Close'
import FilterListIcon from '@material-ui/icons/FilterList'
import SearchIcon from '@material-ui/icons/Search'

const useStyles = makeStyles((theme: Theme) => ({
    bar: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    searchField: {
        minWidth: 220,
        flex: '1 1 240px',
        maxWidth: 420,
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    filterButton: {
        textTransform: 'none',
        fontWeight: 600,
        flexShrink: 0,
    },
    filterButtonActive: {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
    },
    filterCount: {
        height: 18,
        fontSize: 11,
        fontWeight: 700,
        marginLeft: 6,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    resultLabel: {
        marginLeft: 'auto',
        whiteSpace: 'nowrap',
    },
    chips: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    chip: {
        height: 26,
        fontWeight: 500,
        backgroundColor: '#eff6ff',
        color: '#1d4ed8',
        border: '1px solid #bfdbfe',
    },
    approvalChip: {
        height: 26,
        fontWeight: 600,
        backgroundColor: '#fef9c3',
        color: '#854d0e',
        border: '1px solid #fde68a',
    },
    drawer: {
        width: 360,
        maxWidth: '100vw',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 2, 1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    drawerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        minWidth: 0,
    },
    drawerIcon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: '#eff6ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    drawerBody: {
        flex: 1,
        overflow: 'auto',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    drawerFooter: {
        padding: theme.spacing(1.5, 2, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

export const ApprovalCountChip: React.FC<{
    count: number
    onClick: () => void
}> = ({ count, onClick }) => {
    const classes = useStyles()
    if (count < 1) return null
    return (
        <Chip
            size="small"
            clickable
            className={classes.approvalChip}
            label={count === 1 ? '1 needs approval' : `${count} need approval`}
            onClick={onClick}
        />
    )
}

export interface FilterChipItem {
    id: string
    label: string
    onDelete: () => void
}

interface CatalogFilterBarProps {
    searchPlaceholder: string
    searchValue: string
    onSearchChange: (value: string) => void
    onOpenFilters: () => void
    chips: FilterChipItem[]
    onClearAll?: () => void
    resultLabel?: string
    hideSearch?: boolean
    toolbarExtra?: React.ReactNode
}

export const CatalogFilterBar: React.FC<CatalogFilterBarProps> = ({
    searchPlaceholder,
    searchValue,
    onSearchChange,
    onOpenFilters,
    chips,
    onClearAll,
    resultLabel,
    hideSearch = false,
    toolbarExtra,
}) => {
    const classes = useStyles()

    return (
        <Box className={classes.bar}>
            <Box className={classes.row}>
                {!hideSearch && (
                    <TextField
                        className={classes.searchField}
                        placeholder={searchPlaceholder}
                        variant="outlined"
                        size="small"
                        value={searchValue}
                        onChange={(event) => onSearchChange(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
                <Button
                    size="small"
                    variant="outlined"
                    className={`${classes.filterButton} ${
                        chips.length > 0 ? classes.filterButtonActive : ''
                    }`}
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={onOpenFilters}
                >
                    Filters
                    {chips.length > 0 && (
                        <Chip
                            size="small"
                            label={chips.length}
                            className={classes.filterCount}
                        />
                    )}
                </Button>
                {toolbarExtra}
                {resultLabel && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        className={classes.resultLabel}
                    >
                        {resultLabel}
                    </Typography>
                )}
            </Box>
            {chips.length > 0 && (
                <Box className={classes.chips}>
                    {chips.map((chip) => (
                        <Chip
                            key={chip.id}
                            size="small"
                            label={chip.label}
                            onDelete={chip.onDelete}
                            className={classes.chip}
                        />
                    ))}
                    {onClearAll && (
                        <Button size="small" onClick={onClearAll}>
                            Clear all
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    )
}

interface CatalogFilterDrawerProps {
    open: boolean
    onClose: () => void
    title?: string
    activeCount: number
    onClear: () => void
    children: React.ReactNode
}

export const uniqueNamedOptions = (
    items: Array<{ id?: number | null; name?: string | null }>,
) => {
    const names = new Map<number, string>()
    items.forEach((item) => {
        if (item.id != null && item.name) {
            names.set(item.id, item.name)
        }
    })
    return Array.from(names.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
}

export const COI_STATUS_LABELS: Record<string, string> = {
    COMPLIANT: 'Compliant',
    NON_COMPLIANT: 'Non-Compliant',
    EXPIRED: 'Expired',
    EXPIRING_SOON: 'Expiring Soon',
    NOT_YET_EFFECTIVE: 'Not Yet Effective',
    PENDING_REVIEW: 'Needs approval',
}

export const COI_REVIEW_LABELS: Record<string, string> = {
    approved: 'Approved',
    denied: 'Denied',
    awaiting: 'Awaiting decision',
}

interface CatalogSelectFieldProps {
    label: string
    value: string | number
    onChange: (value: string) => void
    options: Array<{ value: string | number; label: string }>
}

export const CatalogSelectField: React.FC<CatalogSelectFieldProps> = ({
    label,
    value,
    onChange,
    options,
}) => (
    <TextField
        label={label}
        variant="outlined"
        size="small"
        select
        fullWidth
        value={value}
        onChange={(event) => onChange(event.target.value)}
    >
        {options.map((option) => (
            <MenuItem key={String(option.value)} value={option.value}>
                {option.label}
            </MenuItem>
        ))}
    </TextField>
)

export const getPropertyTagChip = (
    selectedTagId: number | null,
    tags: UserTag[],
    onClear: () => void,
): FilterChipItem | null => {
    if (selectedTagId == null) return null
    const tag = tags.find((item) => item.id === selectedTagId)
    if (!tag) return null
    return {
        id: 'tag',
        label: `Tag: ${tag.name}`,
        onDelete: onClear,
    }
}

interface PropertyTagFilterFieldProps {
    value: number | null
    onChange: (value: number | null) => void
}

export const PropertyTagFilterField: React.FC<PropertyTagFilterFieldProps> = ({
    value,
    onChange,
}) => {
    const { userTags } = useUserTags()

    return (
        <TextField
            label="Property tag"
            variant="outlined"
            size="small"
            select
            fullWidth
            value={value ?? 'all'}
            onChange={(event) => {
                const next = event.target.value
                onChange(next === 'all' ? null : Number(next))
            }}
        >
            <MenuItem value="all">All properties</MenuItem>
            {userTags.map((tag) => (
                <MenuItem key={tag.id} value={tag.id}>
                    <Box display="flex" alignItems="center">
                        <Box
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: tag.color,
                                marginRight: 8,
                                flexShrink: 0,
                            }}
                        />
                        {tag.name}
                    </Box>
                </MenuItem>
            ))}
        </TextField>
    )
}

export const CatalogFilterDrawer: React.FC<CatalogFilterDrawerProps> = ({
    open,
    onClose,
    title = 'Filters',
    activeCount,
    onClear,
    children,
}) => {
    const classes = useStyles()

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box className={classes.drawer}>
                <Box className={classes.drawerHeader}>
                    <Box className={classes.drawerTitle}>
                        <Box className={classes.drawerIcon}>
                            <FilterListIcon fontSize="small" />
                        </Box>
                        <Box>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 700 }}
                            >
                                {title}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {activeCount === 0
                                    ? 'No filters applied'
                                    : `${activeCount} filter${
                                          activeCount === 1 ? '' : 's'
                                      } applied`}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        style={{ backgroundColor: '#f3f4f6' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box className={classes.drawerBody}>{children}</Box>
                {activeCount > 0 && (
                    <Box className={classes.drawerFooter}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                onClear()
                                onClose()
                            }}
                        >
                            Clear all filters
                        </Button>
                    </Box>
                )}
            </Box>
        </Drawer>
    )
}
